import { useEffect, useState } from "react";
import api from "@services/axios/apiManager";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { useUser } from "@utils/store/user/useUser";
import { useOrder } from "@utils/store/cart/useOrder";
import { buildXContextHeader } from "@utils/api/headers";
import { MONTHLY_CUSTOMER_TYPE, type SupplierData } from "@screen/Home/pages/GasShop/products";
import { normalizeTaxId, normalizePhone } from "@utils/func/supplierIdentity";
import { fetchKioskBootstrap } from "./api/kioskApi";
import { useKiosk } from "./useKiosk";

/**
 * Kiosk 首頁的資料載入：拉 bootstrap、設定配送時段、同步地址、標記熟客。
 * 把副作用集中在這裡，讓 KioskHome 專注於版面組合。
 */
export function useKioskBootstrap() {
  const { cusJwtToken } = useCusAuth();
  const { chooseIdentity, setChooseIdentity, userData, setUserData, accountPhoneLoaded, setAccountPhoneLoaded } = useUser();
  const { setDeliverySlots, setIsOpenNow, setAllowReservation, setHideAmountOnPending, setDeliveryAreas, setSupplierIdentity } = useOrder();
  const { setFirstCheckoutDone, setShowInvoice, setIsMonthly, setAllowElectronicPay, entrySupplierId } = useKiosk();

  const [supplier, setSupplier] = useState<SupplierData | null>(null);
  const [loaded, setLoaded] = useState(false);

  // 無 CIS 新客戶（onboarding 走完、CIS 延到結帳/下單才建）推不出 primary_supplier →
  // 退回「入口瓦斯行」（QR/傳單解析出的 entrySupplierId）做 supplier 預覽，否則商品永遠空。
  const supplierId = chooseIdentity?.primary_supplier?.supplier_id || entrySupplierId || undefined;
  const xContext = buildXContextHeader(chooseIdentity) || "";

  /**
   * 刻意不開全域 loading overlay（useUtilsStore / 鋼瓶品牌頁）。
   *
   * ProductGrid 在 `!loaded` 時就會畫頁內大 spinner，而 loaded 與本次 bootstrap 同步
   * （下面 finally 裡一起結束）→ 讀取期間畫面本來就有指示。再開全域 overlay 會變成：
   * 登入鋼瓶 loading 結束後又跳第二次品牌頁，或與 ProductGrid 藍圈同時出現。
   *
   * 規則：全域 overlay 只給「使用者主動觸發、完成前不該做別的事」的動作（送單、完成設定、
   * 一鍵重訂）；進頁自動讀取一律由頁面自己的 spinner 負責。
   */
  useEffect(() => {
    // 尚未登入或還沒選身分（context）→ 不打 API，但要結束 loaded，避免 ProductGrid 無限轉圈
    if (!cusJwtToken || !supplierId) {
      setLoaded(true);
      return;
    }
    let cancelled = false;
    setLoaded(false);
    // fail-open：重新抓 bootstrap 期間先清空配送範圍提示，避免沿用上一個 supplier（或已過期）的範圍
    // 誤導使用者；沒有範圍資料時 isAddressDeliverable 視為全部可送，不會誤擋。
    setDeliveryAreas([]);
    (async () => {
      try {
        const { supplier: sup, addresses } = await fetchKioskBootstrap({ jwtToken: cusJwtToken, xContext, supplierId });
        if (cancelled) return;
        setSupplier(sup);
        // 賣方身分（版頭顯示「這單跟誰買」）：bootstrap 為主，缺欄位時退回身分的 primary_supplier。
        // 名稱是必要條件——只有統編/電話而沒有名稱沒有意義，整段不顯示。
        const identityName = (sup?.supplier_name || chooseIdentity?.primary_supplier?.supplier_name || "").trim();
        setSupplierIdentity(
          identityName
            ? {
                name: identityName,
                taxId: normalizeTaxId(sup?.tax_id_number ?? chooseIdentity?.primary_supplier?.tax_id_number),
                phone: normalizePhone(sup?.phone ?? chooseIdentity?.primary_supplier?.phone),
              }
            : null,
        );
        // 發票區開關（PW 後台 platform_setting）：=== true 才開，安全預設關
        setShowInvoice(sup?.platform_setting?.show_invoice === true);
        // 電子付款開關（PW 後台 platform_setting）：=== true 才開，安全預設關
        setAllowElectronicPay(sup?.platform_setting?.enable_electronic_payment === true);
        setIsMonthly(sup?.customer_type === MONTHLY_CUSTOMER_TYPE);
        if (sup?.delivery_slots) setDeliverySlots(sup.delivery_slots);
        setIsOpenNow(sup?.is_open_now ?? true);
        // 供應商設定：線上預約開關、隱藏金額開關（=== true 才啟用；與 GasShop 一致）
        setAllowReservation(sup?.allow_customer_reservation === true);
        setHideAmountOnPending(sup?.hide_amount_on_pending === true);
        // 配送範圍：供新增地址時即時提示所選 city/area 是否可配送（未提供時為空 → 全部可送）
        setDeliveryAreas(sup?.delivery_areas ?? []);
        if (sup?.last_order?.gas_list?.length) setFirstCheckoutDone(true);
        // 同步地址清單到 chooseIdentity（與 GasShop 一致）
        if (chooseIdentity && addresses.length > 0) {
          const cur = chooseIdentity.choose_address;
          // onboarding/kiosk 定位塞的「佔位地址」（customer_address_id="gps"/"demo-gps"/"onboarding"）
          // 不是真實 id，建 draft 會被後端擋（customer_address_id must be an integer）。
          // 拿到真實地址清單時自動治癒：以 full_address 對回真實那筆；對不到就取第一筆。
          const curIsReal = !!cur && Number.isInteger(Number(cur.customer_address_id));
          const healed = !cur
            ? addresses[0]
            : curIsReal
              ? cur
              : addresses.find((a: { full_address?: string }) => a.full_address === cur.full_address) || addresses[0];
          const nextChoose = healed;
          // 用 customer_address_id 做穩定比對（不要 JSON.stringify 整個物件——伺服器每次回傳
          // 欄位順序/值可能有微差，會讓比對永遠 false → 每次 fetch 完都 setChooseIdentity →
          // 觸發全店重渲染 → 養出重抓迴圈，直到剛好相等才停）。真的有變才寫。
          const idsOf = (list?: { customer_address_id?: string | number }[]) =>
            (list ?? []).map((a) => String(a.customer_address_id)).join(",");
          // chooseIdentity.address 型別可能是 Address[] 或 string（IdentityContext），非陣列時視為空
          const prevAddrList = Array.isArray(chooseIdentity.address)
            ? chooseIdentity.address
            : undefined;
          const listSame = idsOf(prevAddrList) === idsOf(addresses);
          const chooseSame =
            String(chooseIdentity.choose_address?.customer_address_id) ===
            String(nextChoose?.customer_address_id);
          if (!listSame || !chooseSame) setChooseIdentity({ ...chooseIdentity, address: addresses, choose_address: nextChoose });
        }
      } catch (e) {
        console.error("kiosk bootstrap error", e);
        // fail-open：bootstrap 失敗時清空配送範圍，避免殘留舊資料造成錯誤提示
        setDeliveryAreas([]);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // token / supplier / 身分（context）變動時重抓。context_id 是 2026-09-03 補上的：
    // 同一家瓦斯行如今可有多個身分（決策 A per-supplier context），只看 supplierId 會讓
    // 同行身分切換完全不重抓——地址、上次訂單、月結別全部殘留上一個身分。effect 內的
    // setChooseIdentity 有 listSame/chooseSame 守門且不改 context_id，不會自我觸發迴圈。
  }, [cusJwtToken, supplierId, chooseIdentity?.context_id]);

  // 直接從 LIFF 進 kiosk（沒先逛 HomePage）時 userData 為 null、拿不到聯絡電話 → 主動補抓 /customer/home。
  // 與 GasShop cart 同一做法（phone 來自 customer.main_phone）；確認頁據此自動帶入聯絡電話。
  //
  // accountPhoneLoaded 也是離開條件：LINE 推播邀請註冊的客戶帳號上本來就沒有手機，
  // 抓回來 userData.phone 依然是空的，只看它會讓這支 API 有機會被反覆重抓
  //（也分不出「還沒載入」與「真的沒有」，見 phoneSetupPolicy.ts）。
  useEffect(() => {
    if (!cusJwtToken || !chooseIdentity?.context_id || userData?.phone || accountPhoneLoaded) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/app-api/customer/home", {
          headers: { Authorization: `Bearer ${cusJwtToken}`, "X-Context": buildXContextHeader(chooseIdentity) },
        });
        if (cancelled) return;
        const data = res?.data?.data;
        if (data?.customer) setUserData({ name: data.customer.customer_name, phone: data.customer.main_phone });
        // 成功回來才標記載入完成：失敗時維持 false，結帳補登手機的閘門就不會被誤觸發（fail-open）。
        setAccountPhoneLoaded(true);
      } catch (error) {
        console.error("kiosk customer/home phone fallback error", error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cusJwtToken, chooseIdentity?.context_id, userData?.phone, accountPhoneLoaded, setUserData, setAccountPhoneLoaded]);

  return { supplier, loaded, supplierId, xContext };
}
